export enum FinanceLabStatus {
    Running = "RUNNING",
    Pending = "PENDING",
    Started = "STARTED",
    Success = "SUCCESS",
    Failure = "FAILURE",
}

export enum CapitalProductType {
    EQUITY = "equity",
    VENTURE_DEBT = "venture_debt",
    CASHFLOW_DEBT = "cashflow_debt",
    ABL = "abl",
    FACTORING = "factoring",
    PO_FINANCING = "po_financing",
    SPECIALTY_FINANCE = "specialty_finance",
    GROWTH_DEBT = "growth_debt",
    MCA = "mca",
}

export const CapitalProductTypeLabels: Record<CapitalProductType, string> = {
    [CapitalProductType.EQUITY]: "Equity",
    [CapitalProductType.VENTURE_DEBT]: "Venture Debt",
    [CapitalProductType.CASHFLOW_DEBT]: "Cashflow Debt",
    [CapitalProductType.ABL]: "ABL",
    [CapitalProductType.FACTORING]: "Factoring",
    [CapitalProductType.PO_FINANCING]: "PO Financing",
    [CapitalProductType.SPECIALTY_FINANCE]: "Specialty Finance",
    [CapitalProductType.GROWTH_DEBT]: "Growth Debt",
    [CapitalProductType.MCA]: "MCA",
};

export enum Frequency {
    DAILY = "D",
    WEEKLY = "W",
    MONTHLY = "M",
    QUARTERLY = "Q",
    HALF_YEARLY = "6M",
    YEARLY = "Y",
    UNKNOWN = "U",
}

export const FrequencyLabels: Record<Frequency, string> = {
    [Frequency.DAILY]: "Daily",
    [Frequency.WEEKLY]: "Weekly",
    [Frequency.MONTHLY]: "Monthly",
    [Frequency.QUARTERLY]: "Quarterly",
    [Frequency.HALF_YEARLY]: "Semi-Annually",
    [Frequency.YEARLY]: "Annually",
    [Frequency.UNKNOWN]: "Unknown",
};

export enum FinanceLabField {
    DEAL_CHECK_SIZE = "deal.check_size",
    ORGANIZATION_HEADQUARTERS = "organization.headquarters",
    ORGANIZATION_HEADCOUNT = "organization.headcount",
    ORGANIZATION_INDUSTRY = "organization.industry",
    ORGANIZATION_URLS_WEBSITE = "organization.urls.website",
    ORGANIZATION_LEGAL_NAME = "organization.legal_name",
    ORGANIZATION_ABOUT = "organization.about",
    ORGANIZATION_REVENUE_MODEL = "organization.revenue_model",
    ORGANIZATION_NAICS = "organization.naics",
    ORGANIZATION_CRM_ACCOUNT_ID = "organization.crm_account_id",
    ORGANIZATION_CRM_OPPORTUNITY_ID = "organization.crm_opportunity_id",
    ORGANIZATION_CRM_OPPORTUNITY_NAME = "organization.crm_opportunity_name",
    ORGANIZATION_ICM_ID = "organization.icm_id",
    ORGANIZATION_DUNS_NUMBER = "organization.duns_number",
    DEAL_TRANSACTION_SUMMARY = "deal.transaction_summary",
    DEAL_CAPITAL_PRODUCT = "deal.capital_product",
    DEAL_MATCHING_INVESTMENT_STRATEGIES_SUMMARY = "deal.matching_investment_strategies.summary",
    DEAL_INITIAL_DISBURSEMENT = "deal.initial_disbursement",
    DEAL_TERM = "deal.term",
    DEAL_CURRENT_PAY_TYPE = "deal.current_pay_type",
    DEAL_CURRENT_PAY_P = "deal.current_pay_p",
    DEAL_UP_FRONT_FEE = "deal.up_front_fee",
    DEAL_BROKER_FEE = "deal.broker_fee",
    DEAL_MOIC = "deal.moic",
    DEAL_IRR = "deal.irr",
    DEAL_CURRENT_PAY_CASH_P = "deal.current_pay_cash_p",
    DEAL_MONITORING_FEE = "deal.monitoring_fee",
    DEAL_LEGAL_FEES = "deal.legal_fees",
    DEAL_COMMITMENT_FEE = "deal.commitment_fee",
    DEAL_PREPAYMENT_PENALTY = "deal.prepayment_penalty",
    DEAL_RECURRING_PAYMENT = "deal.recurring_payment",
    DEAL_SECURITY_PACKAGE = "deal.security_package",
    DEAL_CLOSE_DATE_TARGET = "deal.close_date.target",
    DEAL_CLOSE_DATE = "deal.close_date",
    DEAL_FUNDED_DATED = "deal.funded_dated",
    DEAL_OPERATIONAL_CONTROLS = "deal.operational_controls",
    DEAL_COLLECTIONS = "deal.collections",
    DEAL_DISBURSEMENTS = "deal.disbursements",
    DEAL_COVENANTS_SUMMARY = "deal.covenants.summary",
    DEAL_MATERIAL_LEGAL_TERMS = "deal.material_legal_terms",
    DEAL_USE_OF_PROCEEDS = "deal.use_of_proceeds",
    DEAL_CONDITIONS_PRECEDENT_TO_CLOSING = "deal.conditions_precedent_to_closing",
    ORGANIZATION_FINANCIAL_DATA_TOTAL_REVENUE = "organization.financial_data.total_revenue",
    ORGANIZATION_FINANCIAL_DATA_GROSS_MARGIN_P = "organization.financial_data.gross_margin_p",
    ORGANIZATION_FINANCIAL_DATA_EBITDA = "organization.financial_data.ebitda",
    ORGANIZATION_FINANCIAL_DATA_EBITDA_MARGIN_P = "organization.financial_data.ebitda_margin_p",
    ORGANIZATION_FINANCIAL_DATA_CASH = "organization.financial_data.cash",
    ORGANIZATION_FINANCIAL_DATA_SALES_AND_MARKETING_EFFICIENCY = "organization.financial_data.sales_and_marketing_efficiency",
    ORGANIZATION_FINANCIAL_DATA_NET_INCOME = "organization.financial_data.net_income",
    ORGANIZATION_FINANCIAL_DATA_NET_INCOME_MARGIN_P = "organization.financial_data.net_income_margin_p",
    ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS = "organization.financial_data.total_assets",
    ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES = "organization.financial_data.total_liabilities",
    ORGANIZATION_FINANCIAL_DATA_JUNIOR_DEBT_BALANCE_AVG = "organization.financial_data.debt.junior.balance_avg",
    DEAL_INVESTMENT_RATIONALE = "deal.investment_rationale",
    DEAL_RISKS_SUMMARY = "deal.risks.summary",
    ORGANIZATION_ENTITY_STRUCTURE_LIEN_SEARCH_SUMMARY = "organization.entity_structure.lien_search.summary",
    ORGANIZATION_TEAM_KEY_PEOPLE_BACKGROUND_CHECK_SUMMARY = "organization.team.key_people.background_check.summary",
    ORGANIZATION_FINANCIAL_DATA_HISTORICAL_NET_INCOME_TABLE_L3YTTM = "organization.financial_data.historical_net_income_table_l3yttm",
    ORGANIZATION_FINANCIAL_DATA_TOTAL_EQUITY_RAISED = "organization.financial_data.total_equity_raised",
    ORGANIZATION_FINANCIAL_DATA_SENIOR_DEBT_BALANCE_AVG = "organization.financial_data.debt.senior.balance_avg",
    DEAL_CREDIT_COVERAGE = "deal.credit_coverage",
    DEAL_LEVERAGE = "deal.leverage",
    DEAL_INTEREST_COVERAGE = "deal.interest_coverage",
    DEAL_GROSS_DEBT_TO_ASSETS = "deal.gross_debt_to_assets",
    DEAL_GROSS_DEBT_TO_EBITDA = "deal.gross_debt_to_ebitda",
    DEAL_FCCR = "deal.fccr",
    DEAL_DSCR = "deal.dscr",
    DEAL_DEBT_TO_EQUITY = "deal.debt_to_equity",
    ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS_TO_EQUITY = "organization.financial_data.total_assets_to_equity",
    ORGANIZATION_FINANCIAL_DATA_CASH_BURN_MONTHLY = "organization.financial_data.cash_burn.monthly",
    DEAL_SOURCES_AND_USES = "deal.sources_and_uses",
    ORGANIZATION_ENTITY_STRUCTURE_DIAGRAM = "organization.entity_structure.diagram",
    ORGANIZATION_CUSTOMERS_TOP_5 = "organization.customers.top_5",
    ORGANIZATION_COMPETITORS_TOP_5 = "organization.competitors.top_5",
    ORGANIZATION_SUPPLIERS_TOP_5 = "organization.suppliers.top_5",
    ORGANIZATION_DATA_STATUS_TABLE = "organization.data_status.table",
    ORGANIZATION_CAPITAL_STRUCTURE_CAP_TABLE_SUMMARY = "organization.capital_structure.cap_table.summary",
    ORGANIZATION_FINANCIAL_DATA_VALUATION = "organization.financial_data.valuation",
    ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_TOTAL_REVENUE = "organization.financial_data.projections.hum_base_case.total_revenue",
    ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_GROSS_MARGIN_P = "organization.financial_data.projections.hum_base_case.gross_margin_p",
    ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA = "organization.financial_data.projections.hum_base_case.ebitda",
    ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_EBITDA_MARGIN_P = "organization.financial_data.projections.hum_base_case.ebitda_margin_p",
    ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_CASH = "organization.financial_data.projections.hum_base_case.cash",
    ORGANIZATION_FINANCIAL_DATA_PROJECTIONS_HUM_BASE_CASE_SALES_AND_MARKETING_EFFICIENCY = "organization.financial_data.projections.hum_base_case.sales_and_marketing_efficiency",
    DEAL_REPAYMENT_FREQUENCY = "deal.repayment_frequency",
    DEAL_CRM_OPPORTUNITY_ID = "deal.crm_opportunity_id",
    DEAL_CRM_OPPORTUNITY_NAME = "deal.crm_opportunity_name",
}

export type FinanceLabTask = {
    status: FinanceLabStatus;
    taskId: string;
};

export type FinanceLabMeta = {
    displayMsg?: string;
};

export type GenerateFinanceLabMetricsPayload = {
    companyId: string;
};

export type FinanceLabMetric = {
    info: {
        derived: {
            entityId: string;
            entityType: string;
            label: string;
            localLabel: string;
            periodKind: string;
            asOfDate: string;
        };
    };
    description: {
        name: string;
        description: string;
        inputSchemaDescriptions: string;
        fieldType: FinanceLabFieldType;
        resamplingMethod: string;
        composition: string;
    };
    values: Value[];
};

export enum FinanceLabFieldType {
    CURRENCY = "Currency",
    STRING = "String",
    PERCENTAGE = "Percentage",
    RATIO = "Ratio",
    MULTIPLE = "Multiple",
    DATE = "Date",
    BOOLEAN = "Boolean",
    INTEGER = "Integer",
    PERIOD = "Period",
}

export type Value = {
    period: string;
    value: number;
    formattedValue?: string;
};

export enum ResampleTransform {
    YEARLY = "y",
    LAST_12_MONTHS = "l12m",
    MONTHLY_FROM_INCEPTION = "mfi",
}
